import React, { memo } from 'react';
import type { ComponentPropsWithoutRef, MouseEventHandler } from 'react';
import type { LinkProps } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Icon } from 'apps/promo/components/common/Icon/Icon';
import type { IconColor } from 'apps/promo/components/common/Icon/Icon';
import useIsScrolled from 'apps/promo/hooks/useIsScrolled';

import { CommonButton, LinkButton, ReactLinkButton, type DiskButtonSize, type DiskButtonVariant } from './styles';

type LinkComponentProps = ComponentPropsWithoutRef<typeof ReactLinkButton>;
type AnchorComponentProps = ComponentPropsWithoutRef<typeof LinkButton>;
type ButtonComponentProps = ComponentPropsWithoutRef<typeof CommonButton>;

type DiskButtonType = 'tagA' | 'button';

export interface DiskButtonProps {
    iconName?: string;
    iconColor?: IconColor;
    size?: DiskButtonSize;
    variant?: DiskButtonVariant;
    type?: DiskButtonType;
    raised?: boolean;
    disabled?: boolean;
    isInverted?: boolean;
    to?: LinkProps['to'];
    href?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    onMouseDown?: MouseEventHandler<HTMLElement>;
    id?: string;
    title?: string;
    marginRight?: string;
    marginTop?: string;
    labelText?: string;
    dataTestId?: string;
}

const DiskButtonComponent = ({
    iconName = 'cross',
    iconColor = 'darkGray',
    size = 'tight',
    variant,
    type,
    raised,
    disabled,
    isInverted,
    to,
    href,
    onClick,
    onMouseDown,
    id,
    title,
    marginRight,
    marginTop,
    labelText,
    dataTestId,
}: DiskButtonProps) => {
    const isScrolled = useIsScrolled();
    const computedRaised = raised ?? isScrolled;

    if (type === 'tagA') {
        return (
            <LinkButton
                aria-label={labelText}
                variant={variant}
                size={size}
                raised={computedRaised}
                disabled={disabled}
                $isInverted={isInverted}
                $marginRight={marginRight}
                $marginTop={marginTop}
                data-testid={dataTestId}
                href={href}
                onClick={onClick as AnchorComponentProps['onClick']}
                onMouseDown={onMouseDown as AnchorComponentProps['onMouseDown']}
                id={id}
                title={title}
            >
                <Icon name={iconName} color={iconColor} size={size === 'small' ? '16px' : '24px'} />
            </LinkButton>
        );
    }

    if (type === 'button') {
        return (
            <CommonButton
                aria-label={labelText}
                variant={variant}
                type="button"
                size={size}
                raised={computedRaised}
                disabled={disabled}
                $isInverted={isInverted}
                $marginRight={marginRight}
                $marginTop={marginTop}
                data-testid={dataTestId}
                onClick={onClick as ButtonComponentProps['onClick']}
                onMouseDown={onMouseDown as ButtonComponentProps['onMouseDown']}
                id={id}
                title={title}
            >
                <Icon name={iconName} color={iconColor} size={size === 'small' ? '16px' : '24px'} />
            </CommonButton>
        );
    }

    const linkTo = to ?? '#';
    return (
        <ReactLinkButton
            aria-label={labelText}
            variant={variant}
            size={size}
            raised={computedRaised}
            disabled={disabled}
            $isInverted={isInverted}
            $marginRight={marginRight}
            $marginTop={marginTop}
            data-testid={dataTestId}
            to={linkTo}
            onClick={onClick as LinkComponentProps['onClick']}
            onMouseDown={onMouseDown as LinkComponentProps['onMouseDown']}
            id={id}
            title={title}
        >
            <Icon name={iconName} color={iconColor} size={size === 'small' ? '16px' : '24px'} />
        </ReactLinkButton>
    );
};

DiskButtonComponent.propTypes = {
    type: PropTypes.oneOf(['tagA', 'button']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'secondary_light', 'tertiary', 'tertiary_light', 'error', 'errorHint']),
    size: PropTypes.oneOf(['small', 'tight', 'fit']),
    raised: PropTypes.bool,
    disabled: PropTypes.bool,
    isInverted: PropTypes.bool,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    href: PropTypes.string,
    onClick: PropTypes.func,
    iconName: PropTypes.string,
    iconColor: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    marginRight: PropTypes.string,
    marginTop: PropTypes.string,
    labelText: PropTypes.string,
    dataTestId: PropTypes.string,
    onMouseDown: PropTypes.func,
};

DiskButtonComponent.displayName = 'DiskButton';

export const DiskButton = memo(DiskButtonComponent);
